import { useEffect, useState, useCallback } from "react";
import getStockPrice from "../../api/stockPriceApi";
import { variantMapper } from "../../../mappers/variant";
import { useRuntimeConstants } from "../runtimeConstants/RuntimeConstantsContext";
import { productIsInStock } from "../../utils/product";
import getWindow from "../../utils/window";
import { ERROR_TYPE, logError } from "../../utils/newRelicLogger";
import getRestockingSoonDetails from "./getRestockingSoonDetails";

const filterInvalidVariants = (products) => {
  return products.map((product) => ({
    ...product,
    variants: product.variants.filter(({ price }) => !!price),
  }));
};

export const usePriceAndProductData = ({ retry, retryStockPriceCall }) => {
  const { product, stockPriceApiUrl, serverSideStockPriceData, storeCode } =
    useRuntimeConstants();
  const [hasError, setHasError] = useState(false);

  const processStockPriceData = useCallback(
    (stockPriceProducts) => {
      if (!stockPriceProducts) {
        return null;
      }

      const hasMultiProducts = !!product.products;
      const productList = hasMultiProducts
        ? [...product.products]
        : [{ ...product }];

      const mappedProducts = productList.map((productItem) => {
        const stockPriceProduct = stockPriceProducts.find(
          ({ productId }) => productItem.id === productId,
        );

        const matchedVariants = productItem.variants.map((serverVariant) => {
          const stockPriceVariant = stockPriceProduct?.variants.find(
            ({ id }) => serverVariant.variantId === id,
          );

          if (!stockPriceVariant) {
            return serverVariant;
          }

          const stockPriceVariantHasPrice = !!stockPriceVariant.price;
          const mappedStockPriceVariant = variantMapper(
            stockPriceVariant,
            stockPriceVariantHasPrice,
          );

          const {
            price,
            isInStock,
            seller,
            source,
            isLowInStock,
            showShippedByMessage,
          } = mappedStockPriceVariant;

          return {
            ...serverVariant,
            price,
            isInStock,
            seller,
            source,
            isLowInStock,
            showShippedByMessage,
          };
        });

        const stockStatus =
          matchedVariants[0].isInStock !== undefined
            ? { isInStock: productIsInStock(stockPriceProduct) }
            : {};

        const price = stockPriceProduct?.productPrice || null;

        const restockingSoonDetails = getRestockingSoonDetails({
          stockPriceProduct,
          stockStatus,
        });

        return {
          ...productItem,
          ...stockStatus,
          restockingSoonDetails,
          price,
          variants: [...matchedVariants],
        };
      });

      const mappedProductsWithFilteredVariants =
        filterInvalidVariants(mappedProducts);

      return hasMultiProducts
        ? {
            ...product,
            products: mappedProductsWithFilteredVariants,
          }
        : mappedProductsWithFilteredVariants[0];
    },
    [product],
  );

  const [products, setProducts] = useState(() => {
    if (serverSideStockPriceData) {
      return processStockPriceData(serverSideStockPriceData);
    }
    return null;
  });

  const hasServerSideStockPrice = !!serverSideStockPriceData && !retry;

  useEffect(() => {
    if (hasServerSideStockPrice && !retry) {
      return;
    }

    let isMounted = true;

    getStockPrice(product, retry, storeCode)
      .then((stockPriceProducts) => {
        if (!isMounted || !stockPriceProducts) {
          return;
        }

        const processedData = processStockPriceData(stockPriceProducts);
        setProducts(processedData);
        setHasError(false);

        getWindow().asos.eventBus.emit("productAndPriceData", {
          ...product,
          products: processedData,
        });
      })
      .catch((error) => {
        logError(ERROR_TYPE.StockPriceError, {
          action: "getStockPrice",
          caller: "stockPrice",
          apiName: "stockPrice",
          url: stockPriceApiUrl,
          message: error.message || error.statusText,
          status: error.status,
          response: error.data,
          responseHeaders: error.headers,
        });
        if (!isMounted) return;
        setHasError(true);
      })
      .finally(() => {
        if (isMounted) {
          retryStockPriceCall(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    product,
    retry,
    retryStockPriceCall,
    stockPriceApiUrl,
    processStockPriceData,
    hasServerSideStockPrice,
    storeCode,
  ]);

  useEffect(() => {
    if (products) {
      getWindow().asos.eventBus.emit("productAndPriceData", products);
    }
  }, [products]);

  return { products, hasError };
};
